import React, { useState, useEffect, useMemo } from 'react'
import type { Block, Variable } from '../types'
import { CheckCircle2, Circle, ChevronDown, ChevronUp, X, Trophy, Sparkles } from 'lucide-react'

const STORAGE_KEY = 'explaina_onboarding_dismissed'

const INTERACTIVE_TYPES = new Set([
  'slider', 'toggle', 'dropdown', 'tabs', 'number_input', 'button', 'steps', 'poll', 'quiz',
])

interface OnboardingChecklistProps {
  blocks: Block[]
  variables: Variable[]
  published: boolean
}

interface Step {
  label: string
  done: boolean
}

export default function OnboardingChecklist({ blocks, variables, published }: OnboardingChecklistProps) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true')
  const [collapsed, setCollapsed] = useState(false)
  const [showReward, setShowReward] = useState(false)
  const [xpVisible, setXpVisible] = useState(false)

  const steps: Step[] = useMemo(() => [
    { label: 'Create a demo', done: true },
    { label: 'Add an interactive block', done: blocks.some((b) => INTERACTIVE_TYPES.has(b.type)) },
    { label: 'Connect a variable', done: blocks.some((b) => b.content?.includes('@')) },
    { label: 'Publish your demo', done: published },
  ], [blocks, published])

  const completedCount = steps.filter((s) => s.done).length
  const allDone = completedCount === steps.length

  useEffect(() => {
    if (allDone && !showReward) {
      setShowReward(true)
      setCollapsed(false)
      // Trigger XP animation after a short delay
      setTimeout(() => setXpVisible(true), 300)
      // Auto-dismiss after 5s (more time to enjoy the reward)
      setTimeout(() => {
        localStorage.setItem(STORAGE_KEY, 'true')
        setDismissed(true)
      }, 5000)
    }
  }, [allDone, showReward])

  if (dismissed) return null

  const progress = (completedCount / steps.length) * 100

  if (showReward) {
    return (
      <div className="fixed bottom-6 right-6 z-40 w-72 rounded-xl border border-primary/30 bg-gradient-to-br from-white to-primary/5 shadow-xl overflow-hidden">
        {/* Shimmer effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent animate-[shimmer_2s_ease-in-out_infinite]" style={{ transform: 'translateX(-100%)', animation: 'shimmer 2s ease-in-out infinite' }} />
        </div>

        <div className="relative px-5 py-5 text-center">
          {/* Badge */}
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/30">
            <Trophy className="h-7 w-7 text-white" />
          </div>

          {/* Title */}
          <p className="text-sm font-bold text-slate-900">First Demo Published!</p>

          {/* XP counter */}
          <div className={`mt-2 transition-all duration-700 ${xpVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-bold text-primary">
              <Sparkles className="h-4 w-4" />
              +50 XP
            </span>
          </div>

          <p className="mt-3 text-[11px] text-slate-400">Badge unlocked — more coming soon</p>
        </div>

        {/* Progress bar full */}
        <div className="h-1 bg-primary/20">
          <div className="h-full bg-primary w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 w-72 rounded-xl border border-slate-200 bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          Getting Started
          {collapsed ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-400">{completedCount}/{steps.length}</span>
          <button onClick={() => { localStorage.setItem(STORAGE_KEY, 'true'); setDismissed(true) }} className="p-0.5 text-slate-400 hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-100">
        <div className="h-full bg-[#5048e5] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {/* Steps */}
      {!collapsed && (
        <div className="px-4 py-3 space-y-2.5">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-2.5">
              {step.done ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-slate-300" />
              )}
              <span className={`text-sm ${step.done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
